import React, { FC } from 'react';

import { observer } from 'mobx-react';
import classNames from 'classnames';

import BatchDeleteButton from './BatchDeleteButton';
import BatchDownloadButton from './BatchDownloadButton';
import BatchShareButton from './BatchShareButton';
import OpenUploaderButton from './OpenUploaderButton';
import CreateFolderButton from './CreateFolderButton';
import OpenAuditsButton from './OpenAuditsButton';
import OpenFolderInfoButton from './OpenFolderInfoButton';
import { useStores } from '../../../hooks';
import appConfig from '../../../../config/env';
import ability, { SpecterXRules } from '../../../../config/ability';
import styles from './ExtraContent.module.scss';

const ExtraContent: FC = observer(() => {
    const {
        filesListStore: {
            isMyFiles,
            isShared,
            selectedItemsCount,
            currentNonRootKey,
            currentFolder,
            displayFilesType,
            currentFolderId,
            setChosenTableFileId,
            hasBatchDownload,
        },
        userStore: { isExternal, isAuditor },
    } = useStores();

    const showViewInfo = currentFolder?.is_workspace && currentFolder?.menu?.edit_shares;
    const showSendButton = isShared && isExternal && !currentNonRootKey;
    const showAddFiles = isMyFiles || currentFolder?.menu?.options?.includes('add_files');
    const hasAbilityToSeeLogs = displayFilesType !== 'sharedFiles' || currentFolder?.menu?.options?.includes('audits');
    const showLogsFiles = hasAbilityToSeeLogs && currentFolderId;
    const areSeveralItemsSelected = selectedItemsCount > 1;
    // Only allow bulk delete in MyFiles or in folders where the user has manage-access
    // (e.g. workspace co-owner). Plain Share-with-Me must not expose Delete.
    const canBatchDelete = isMyFiles || !!currentFolder?.menu?.edit_shares;
    // Bulk sharing is only supported in MyFiles: selected files on Shared with Me / All Files
    // pages can have mixed policies/permissions, so not every selected file is actually
    // shareable there, even for folders with manage-access.
    const canBatchShare = isMyFiles;
    // Must match the conditions under which App mounts CreateFolderModal
    // (hasAddingFiles && hasCreateFolder) — otherwise the button would render
    // for users (external/auditor/no own-files permission) with no modal to open.
    const canCreateFolder = showAddFiles
        && !appConfig.DISABLE_CREATE_FOLDER
        && !isExternal
        && !isAuditor
        && ability.can(SpecterXRules.SpecterxOwnFiles, 'ALL');
    const hasContent = showSendButton || areSeveralItemsSelected || showAddFiles;

    return (
        <div className={classNames(styles.wrapper, { [styles['has-content']]: hasContent })}>
            {areSeveralItemsSelected && canBatchShare && (
                <BatchShareButton />
            )}
            {hasBatchDownload && (
                <BatchDownloadButton />
            )}
            {areSeveralItemsSelected && canBatchDelete && (
                <BatchDeleteButton />
            )}
            {showAddFiles && <OpenUploaderButton />}
            {canCreateFolder && <CreateFolderButton />}
            {showLogsFiles && <OpenAuditsButton currentFolderId={currentFolderId} />}
            {showViewInfo
                && (
                    <OpenFolderInfoButton
                        setChosenTableFileId={setChosenTableFileId}
                        currentFolderId={currentFolderId}
                    />
                )}
        </div>
    );
});

export default ExtraContent;
